import { NavLink } from 'react-router-dom';
import { canlistTools } from 'components/Tools/helpers';
import { Permissions } from 'components/User/types';
import { useContext } from 'react';
import ApplicationContext from 'components/Application/Context';
import { trackPageVisit } from 'components/PostHog/pageVisitTrack';
import posthog from 'posthog-js';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useShowcaseLogin } from '../../../utils/showcaseLogin';

type AppHeaderMenuProps = {
  permissions: Permissions;
};

// eslint-disable-next-line max-lines-per-function
function AppHeaderMenu({ permissions }: Readonly<AppHeaderMenuProps>) {
  const { user } = useContext(ApplicationContext);
  const { handleShowcaseLogin, tokenLoading } = useShowcaseLogin();
  const hasWatchProductionsEnabled = useFeatureFlagEnabled(
    'WatchProductionsSearch',
  );

  return (
    <ul className="app-header__primary-menu">
      {permissions.productions?.list && (
        <li className="app-header__menu-item">
          <NavLink
            onClick={() => trackPageVisit({ user, page: 'Search' })}
            to="/search"
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__menu-link${isActive ? ' is-active' : ''}`
            }
            data-testid="search-navlink"
          >
            Search
          </NavLink>
        </li>
      )}
      {permissions.productions?.manage && (
        <li className="app-header__menu-item hide_menu">
          <NavLink
            onClick={() => trackPageVisit({ user, page: 'Discovery' })}
            to="/discovery"
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__menu-link ${isActive ? ' is-active' : ''} discovery`
            }
            data-testid="discovery-navlink"
          >
            Discovery
          </NavLink>
        </li>
      )}
      {permissions.campaigns?.list && (
        <li className="app-header__menu-item">
          <NavLink
            onClick={() => trackPageVisit({ user, page: 'Campaigns' })}
            to="/campaigns/list"
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__menu-link${isActive ? ' is-active' : ''}`
            }
            data-testid="campaings-navlink"
          >
            Campaigns
          </NavLink>
        </li>
      )}
      {permissions.productions?.manage && (
        <li className="app-header__menu-item">
          <NavLink
            onClick={() => trackPageVisit({ user, page: 'Productions' })}
            to="/productions/list"
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__menu-link${isActive ? ' is-active' : ''}`
            }
            data-testid="productions-navlink"
          >
            Productions
          </NavLink>
        </li>
      )}
      {permissions.productions?.manage && hasWatchProductionsEnabled && (
        <li className="app-header__menu-item">
          <NavLink
            onClick={() => trackPageVisit({ user, page: 'Watch Productions' })}
            to="/watch-productions"
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__menu-link${isActive ? ' is-active' : ''}`
            }
            data-testid="watch-productions-navlink"
          >
            Watch Productions
          </NavLink>
        </li>
      )}

      {permissions.deliverables?.manage && (
        <li className="app-header__menu-item">
          <NavLink
            onClick={() => trackPageVisit({ user, page: 'Deliverables' })}
            to="/deliverables/list"
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__menu-link${isActive ? ' is-active' : ''}`
            }
            data-testid="deliverables-navlink"
          >
            Deliverables
          </NavLink>
        </li>
      )}

      {(permissions.reports?.list || permissions.placements?.manage) && (
        <li className="app-header__menu-item">
          <NavLink
            onClick={() => trackPageVisit({ user, page: 'Placements' })}
            to="/placements/list"
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__menu-link${isActive ? ' is-active' : ''}`
            }
            data-testid="placements-navlink"
          >
            Placements
          </NavLink>
        </li>
      )}

      {permissions.sources?.list && (
        <li className="app-header__menu-item">
          <NavLink
            onClick={() => trackPageVisit({ user, page: 'Sources' })}
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__menu-link${isActive ? ' is-active' : ''}`
            }
            to="/sources/list"
            data-testid="sources-navlink"
          >
            Sources
          </NavLink>
        </li>
      )}
      {permissions.sources?.list && (
        <li className="app-header__menu-item">
          <NavLink
            onClick={() => trackPageVisit({ user, page: 'Scan' })}
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__menu-link${isActive ? ' is-active' : ''}`
            }
            to="/scan"
            data-testid="scan-navlink"
          >
            Scan
          </NavLink>
        </li>
      )}

      {canlistTools(permissions) && (
        <li className="app-header__menu-item">
          <NavLink
            onClick={() => trackPageVisit({ user, page: 'Tools' })}
            to="/tools"
            className={({ isActive }: { isActive: boolean }) =>
              `app-header__menu-link${isActive ? ' is-active' : ''}`
            }
            data-testid="tools-navlink"
          >
            Tools
          </NavLink>
        </li>
      )}
      {(user?.role === 'internal' || user?.role === 'admin') && (
        <li className="app-header__menu-item">
          <button
            type="button"
            onClick={async () => {
              posthog.capture('showcase_menu_clicked', {
                userId: user?.id,
                location: 'header_menu',
              });
              await handleShowcaseLogin();
            }}
            disabled={tokenLoading}
            className="app-header__menu-link"
            data-testid="showcase-navlink"
            style={{
              background: 'none',
              border: 'none',
              font: 'inherit',
              padding: 0,
              cursor: tokenLoading ? 'not-allowed' : 'pointer',
              opacity: tokenLoading ? 0.6 : 1,
            }}
          >
            🏆 Showcase
            {tokenLoading && (
              <span style={{ marginLeft: '8px' }}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  style={{
                    animation: 'spin 1s linear infinite',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                  }}
                >
                  <style>
                    {'@keyframes spin { to { transform: rotate(360deg); } }'}
                  </style>
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    strokeDasharray="25 75"
                  />
                </svg>
              </span>
            )}
          </button>
        </li>
      )}
    </ul>
  );
}

export default AppHeaderMenu;
